import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Typography,
  message,
  Steps,
  notification,
} from "antd";
import { useHistory } from "react-router-dom";

// ========== AAPKE PURANE HEADER / FOOTER ==========
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

import JourneyForm from "../components/forms/JourneyForm";
import VehiclePlans from "../components/forms/VehiclesForm";
import Summary from "../components/forms/Summary";
import MapWithRoute from "../components/maps/MapWithRoute";
import AddonPlans from "../components/forms/AddonsForm";
import CheckoutForm from "../components/forms/CheckoutForm";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment-timezone";

import { getVehiclesTypes } from "../api/vehicles-type.service";
import { createBooking } from "../api/booking.service";
import { getAddOns } from "../api/add-on.service";

import { useCurrencyConverter } from "../context/currencyHook";
import { useCurrency } from "../context/CurrencyContext";
import config from "../config";

const stripePromise = loadStripe(config.STRIPE_PUBLIC_KEY);
const { Title } = Typography;

export default function AirportTransfers() {
  const history = useHistory();
  const convertCurrency = useCurrencyConverter();
  const { currency } = useCurrency();

  const formRef = useRef();
  const [current, setCurrent] = useState(0);

  const [directions, setDirections] = useState(null);
  const [totalDistance, setTotalDistance] = useState("0 Km");
  const [totalDuration, setTotalDuration] = useState("0 mins");

  const [directionsReturn, setDirectionsReturn] = useState(null);
  const [totalDistanceReturn, setTotalDistanceReturn] = useState("0 Km");
  const [totalDurationReturn, setTotalDurationReturn] = useState("0 mins");

  const [amount, setAmount] = useState(0);
  const [discountedAmount, setDiscountedAmount] = useState(null);

  const [driversInstructions, setDriversInstructions] = useState("");
  const [driversInstructionsReturn, setDriversInstructionsReturn] = useState("");

  const [formData, setFormData] = useState({
    passengerCount: 1,
    luggageCount: "",
    returnPassengerCount: 1,
    returnLuggageCount: "",
    type: "oneway",
    pickupType: "airport",
    additionalPickupType: ["address"],
    returnAdditionalPickupType: ["address"],
    destinationType: "address",
    returnPickupType: "airport",
    returnDestinationType: "address",
    additionalPickups: [],
    returnAdditionalPickups: [],
  });

  const [additionalPickupType, setAdditionalPickupType] = useState(formData.additionalPickupType);
  const [returnAdditionalPickupType, setReturnAdditionalPickupType] = useState(formData.returnAdditionalPickupType);

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVehicleReturn, setSelectedVehicleReturn] = useState(null);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [selectedAddonReturn, setSelectedAddonReturn] = useState(null);

  const [vehicles, setVehicles] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorBooking, setErrorBooking] = useState(false);

  const [onewayMinutes, setOnewayMinutes] = useState(0);
  const [returnMinutes, setReturnMinutes] = useState(0);

  // Fetch Vehicles
  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const data = await getVehiclesTypes();
      setVehicles(data?.data || []);
    } catch (err) {
      notification.error({ message: "Failed to fetch vehicles" });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Addons
  const fetchAddons = async () => {
    try {
      setLoading(true);
      const data = await getAddOns();
      setAddons(data?.data || []);
    } catch (err) {
      notification.error({ message: "Failed to fetch addons" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (current === 1 && vehicles.length === 0) fetchVehicles();
  }, [current]);

  useEffect(() => {
    if (current === 2 && addons.length === 0) fetchAddons();
  }, [current]);

  // Keep formData in sync
  useEffect(() => {
    setFormData((prev) => ({ ...prev, selectedVehicle, selectedVehicleReturn }));
  }, [selectedVehicle, selectedVehicleReturn]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, selectedAddon, selectedAddonReturn }));
  }, [selectedAddon, selectedAddonReturn]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, driversInstructions, driversInstructionsReturn }));
  }, [driversInstructions, driversInstructionsReturn]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, totalDistance, totalDuration }));
  }, [totalDistance, totalDuration]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, totalDistanceReturn, totalDurationReturn }));
  }, [totalDistanceReturn, totalDurationReturn]);

  // Directions - One way
  const handleFormSubmit = ({ pickup, additionalPickup, dropoff }) => {
    if (!window.google?.maps) return;

    const directionsService = new window.google.maps.DirectionsService();
    const request = {
      origin: pickup,
      destination: dropoff,
      travelMode: window.google.maps.TravelMode.DRIVING,
    };

    if (additionalPickup?.length) {
      request.waypoints = additionalPickup
        .filter((f) => f?.pickup)
        .map((elem) => ({ location: elem.pickup, stopover: true }));
    }

    directionsService.route(request, (result, status) => {
      if (status === "OK") {
        setDirections(result);
        const legs = result.routes[0].legs;
        const totalDist = legs.reduce((sum, leg) => sum + leg.distance.value, 0);
        const totalDur = legs.reduce((sum, leg) => sum + leg.duration.value, 0);
        const hours = Math.floor(totalDur / 3600);
        const minutes = Math.floor((totalDur % 3600) / 60);

        setOnewayMinutes(totalDur / 60);
        setTotalDistance(`${(totalDist / 1000).toFixed(1)} Km`);
        setTotalDuration(`${hours > 0 ? hours + "h " : ""}${minutes}m`);
      }
    });
  };

  // Directions - Return
  const handleFormSubmitReturn = ({ pickup, additionalPickup, dropoff }) => {
    if (!window.google?.maps) return;

    const directionsService = new window.google.maps.DirectionsService();
    const request = {
      origin: pickup,
      destination: dropoff,
      travelMode: window.google.maps.TravelMode.DRIVING,
    };

    if (additionalPickup?.length) {
      request.waypoints = additionalPickup
        .filter((f) => f?.pickup)
        .map((elem) => ({ location: elem.pickup, stopover: true }));
    }

    directionsService.route(request, (result, status) => {
      if (status === "OK") {
        setDirectionsReturn(result);
        const legs = result.routes[0].legs;
        const totalDist = legs.reduce((sum, leg) => sum + leg.distance.value, 0);
        const totalDur = legs.reduce((sum, leg) => sum + leg.duration.value, 0);
        const hours = Math.floor(totalDur / 3600);
        const minutes = Math.floor((totalDur % 3600) / 60);

        setReturnMinutes(totalDur / 60);
        setTotalDistanceReturn(`${(totalDist / 1000).toFixed(1)} Km`);
        setTotalDurationReturn(`${hours > 0 ? hours + "h " : ""}${minutes}m`);
      }
    });
  };

  const handleShowMap = () => {
    if (formRef.current) {
      const values = formRef.current.values();
      if (values.pickup && values.destination) {
        handleFormSubmit({
          pickup: values.pickup,
          additionalPickup: values.additionalPickups,
          dropoff: values.destination,
        });
      } else {
        message.error("Please enter pickup and destination");
      }
    }
  };

  const handleShowMapReturn = () => {
    if (formRef.current) {
      const values = formRef.current.values();
      if (values.returnPickup && values.returnDestination) {
        handleFormSubmitReturn({
          pickup: values.returnPickup,
          additionalPickup: values.returnAdditionalPickups,
          dropoff: values.returnDestination,
        });
      } else {
        message.error("Please enter return pickup and destination");
      }
    }
  };

  // Next Button
  const next = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (current === 0) {
      if (formRef.current) {
        const { valid, values, errors } = await formRef.current.validate();
        if (valid) {
          const date = moment.tz(values.date, "Asia/Karachi");
          const time = moment.tz(values.time, "HH:mm", "Asia/Karachi");
          const dateTimeInUTC = date.clone().hour(time.hour()).minute(time.minute()).utc();

          let returnDateTimeInUTC = null;
          if (values.type === "return") {
            const dateR = moment.tz(values.returnDate, "Asia/Karachi");
            const timeR = moment.tz(values.returnTime, "HH:mm", "Asia/Karachi");
            returnDateTimeInUTC = dateR.clone().hour(timeR.hour()).minute(timeR.minute()).utc();
          }

          setFormData({
            ...values,
            dateTime: dateTimeInUTC.format(),
            returnDateTime: returnDateTimeInUTC ? returnDateTimeInUTC.format() : null,
          });

          handleShowMap();
          if (values.type === "return") handleShowMapReturn();

          setCurrent(1);
        } else {
          message.error("Please fill all required fields");
          console.log(errors);
        }
      }
    } else if (current === 1) {
      if (formData.type === "return") {
        if (!selectedVehicle || !selectedVehicleReturn) {
          message.error("Please select vehicle for both journeys");
        } else setCurrent(2);
      } else {
        if (!selectedVehicle) message.error("Please select a vehicle");
        else setCurrent(2);
      }
    } else {
      setCurrent(current + 1);
    }
  };

  const prev = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrent(current - 1);
  };

  // Booking
  const handleSuccess = (discountCode) => {
    const data = { ...formData, totalDistance, totalDuration, totalDistanceReturn, totalDurationReturn };

    const body = {
      passenger: {
        name: data?.name || null,
        email: data?.email || null,
        phone: data?.phone || null,
      },
      journey: {
        driversInstructions: driversInstructions || "",
        driversInstructionsReturn: driversInstructionsReturn || "",
        type: data?.type || null,
        oneway: {
          dateTime: data?.dateTime,
          pickupType: data?.pickupType,
          pickup: data?.pickup,
          destinationType: data?.destinationType,
          destination: data?.destination,
          additionalPickups: data?.additionalPickups?.filter((f) => Object.keys(f).length > 0) || null,
          passengerCount: data?.passengerCount,
          luggageCount: data?.luggageCount,
          flightNumber: data?.flightNumber,
          totalDistance: data?.totalDistance,
          totalDuration: data?.totalDuration,
          selectedVehicle: data?.selectedVehicle,
        },
        return: {
          dateTime: data?.returnDateTime,
          pickupType: data?.returnPickupType,
          pickup: data?.returnPickup,
          destinationType: data?.returnDestinationType,
          destination: data?.returnDestination,
          additionalPickups: data?.returnAdditionalPickups?.filter((f) => Object.keys(f).length > 0) || null,
          passengerCount: data?.returnPassengerCount,
          luggageCount: data?.returnLuggageCount,
          flightNumber: data?.returnFlightNumber,
          totalDistance: data?.totalDistanceReturn,
          totalDuration: data?.totalDurationReturn,
          selectedVehicle: data?.selectedVehicleReturn,
        },
      },
    };

    localStorage.setItem("confirmedPaymentCautionBookingBody", JSON.stringify(body));
    handleBooking(body, discountCode);
  };

  const handleBooking = async (body, discountCode) => {
    setLoading(true);
    setErrorBooking(false);

    try {
      const convertedAmount = convertCurrency(amount);
      const finalDiscountedAmount =
        discountedAmount != null ? convertCurrency(discountedAmount) : convertedAmount;

      let summaryObj = {
        ...formData,
        totalDistance,
        totalDuration,
        totalDistanceReturn,
        totalDurationReturn,
        rideFare: Math.ceil((selectedVehicle?.perMinutePricePKR || 0) * onewayMinutes),
        rideFareReturn: Math.ceil((selectedVehicleReturn?.perMinutePricePKR || 0) * returnMinutes),
        amount: convertedAmount,
        discountedAmount: finalDiscountedAmount,
        currency,
        discountCode: discountCode || "-",
      };

      const response = await createBooking({ ...body, summaryObj });
      notification.success({ message: "Booking created successfully!" });
      localStorage.removeItem("confirmedPaymentCautionBookingBody");

      summaryObj = {
        ...summaryObj,
        oneWayReferenceNo: response?.data[0]?.referenceNo,
        returnReferenceNo: response?.data[1]?.referenceNo,
      };

      localStorage.setItem("summaryObj", JSON.stringify(summaryObj));
      setTimeout(() => history.push("/confirmation"), 500);
    } catch (err) {
      setErrorBooking(true);
      notification.error({ message: "Failed to create booking." });
    } finally {
      setLoading(false);
    }
  };

  const retryBooking = () => {
    const data = localStorage.getItem("confirmedPaymentCautionBookingBody");
    if (data) handleBooking(JSON.parse(data));
  };

  const steps = [
    {
      title: <span style={{ color: "black", fontWeight: "bold" }}>Journey</span>,
      content: (
        <JourneyForm
          ref={formRef}
          initialValues={formData}
          destination={formData?.destination}
          returnDestination={formData?.returnDestination}
          additionalPickups={formData?.additionalPickups}
          returnAdditionalPickups={formData?.returnAdditionalPickups}
          additionalPickupType={additionalPickupType}
          setAdditionalPickupType={setAdditionalPickupType}
          returnAdditionalPickupType={returnAdditionalPickupType}
          setReturnAdditionalPickupType={setReturnAdditionalPickupType}
          handleShowMap={handleShowMap}
          handleShowMapReturn={handleShowMapReturn}
          directions={directions}
          directionsReturn={directionsReturn}
          data={{ totalDistance, totalDuration }}
          dataReturn={{ totalDistance: totalDistanceReturn, totalDuration: totalDurationReturn }}
        />
      ),
    },
    {
      title: <span style={{ color: "black", fontWeight: "bold" }}>Vehicle</span>,
      content: (
        <VehiclePlans
          onewayMinutes={onewayMinutes}
          returnMinutes={returnMinutes}
          loading={loading}
          vehicles={vehicles}
          setSelectedVehicle={setSelectedVehicle}
          selectedVehicle={selectedVehicle}
          setSelectedVehicleReturn={setSelectedVehicleReturn}
          selectedVehicleReturn={selectedVehicleReturn}
          isReturn={formData.type === "return"}
        />
      ),
    },
    {
      title: <span style={{ color: "black", fontWeight: "bold" }}>Add-ons</span>,
      content: (
        <AddonPlans
          journeyType={formData.type}
          loading={loading}
          addons={addons}
          setSelectedAddon={setSelectedAddon}
          selectedAddon={selectedAddon}
          setSelectedAddonReturn={setSelectedAddonReturn}
          selectedAddonReturn={selectedAddonReturn}
        />
      ),
    },
    {
      title: <span style={{ color: "black", fontWeight: "bold" }}>Summary</span>,
      content: (
        <Summary
          setAmount={setAmount}
          rideFare={Math.ceil((selectedVehicle?.perMinutePricePKR || 0) * onewayMinutes)}
          rideFareReturn={Math.ceil((selectedVehicleReturn?.perMinutePricePKR || 0) * returnMinutes)}
          setDriversInstructions={setDriversInstructions}
          setDriversInstructionsReturn={setDriversInstructionsReturn}
          data={{
            ...formData,
            totalDistance,
            totalDuration,
            totalDistanceReturn,
            totalDurationReturn,
          }}
          map={
            <MapWithRoute
              height="250px"
              style={{ margin: 0, marginTop: "0.5rem" }}
              directions={directions}
              data={{ totalDistance, totalDuration }}
            />
          }
          mapReturn={
            <MapWithRoute
              height="250px"
              style={{ margin: 0, marginTop: "0.5rem" }}
              directions={directionsReturn}
              data={{ totalDistance: totalDistanceReturn, totalDuration: totalDurationReturn }}
            />
          }
        />
      ),
    },
    {
      title: <span style={{ color: "black", fontWeight: "bold" }}>Payment</span>,
      content: (
        <Elements stripe={stripePromise}>
          <CheckoutForm
            amount={amount}
            discountedAmount={discountedAmount}
            setDiscountedAmount={setDiscountedAmount}
            retryBooking={retryBooking}
            errorBooking={errorBooking}
            handleSuccess={handleSuccess}
          />
        </Elements>
      ),
    },
  ];

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      {/* ========== AAPKA PURANA HEADER ========== */}
      <Header />

      <main className="flex-grow">
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          {/* Steps with #00401A */}
          <Steps
            current={current}
            items={items}
            style={{
              backgroundColor: "#ffffff",
              border: "3px solid #00401A",
              padding: "1rem",
              borderRadius: "1rem",
              marginBottom: "1.5rem",
            }}
          />

          <div
            style={{
              textAlign: "center",
              color: "#00401A",
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "1rem",
              boxShadow: "rgba(0, 0, 0, 0.15) 0px 5px 15px",
            }}
          >
            {steps[current].content}
          </div>

          <div style={{ marginTop: 24 }} className="flex justify-end">
            {current > 0 && (
              <Button
                style={{ margin: "0 8px", borderColor: "#00401A", color: "#00401A" }}
                onClick={prev}
              >
                Previous
              </Button>
            )}

            {current < steps.length - 1 && (
              <Button
                type="primary"
                style={{ backgroundColor: "#00401A", borderColor: "#00401A" }}
                onClick={next}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* ========== AAPKA PURANA FOOTER ========== */}
      <Footer />
    </div>
  );
}